<app-legal-page-layout
  kicker="Legal"
  title="Privacy Policy"
  description="How Safeword collects, uses, shares and protects your personal data, and your rights under UK GDPR."
  [metaLines]="['Operated by Hove Retail Limited', 'Version 1.0', 'Last reviewed: 17 July 2026']"
  [sections]="sections">

  <p>
    This Privacy Policy explains how Safeword (operated by Hove Retail Limited) collects, uses, shares, and protects
    your personal data, and sets out your rights under UK GDPR and the Data Protection Act 2018. It should be read
    alongside our <a routerLink="/legal/cookie-policy">Cookie Policy</a>,
    <a routerLink="/legal/terms">Terms of Service</a>, <a routerLink="/legal/community-guidelines">Community
      Guidelines</a>, and <a routerLink="/legal/age-verification">Age Verification &amp; Restricted Access
      Policy</a>.
  </p>

  <section id="definitions">
    <h2>Definitions</h2>
    <p>
      For the purposes of this Privacy Policy, the following terms have the meanings set out below unless the
      context requires otherwise.
    </p>

    <h3>Account</h3>
    <p>An account registered by a Member to access and use the Safeword platform and its Services.</p>

    <h3>AI</h3>
    <p>
      Artificial Intelligence and automated technologies used by Safeword to assist with security, fraud
      prevention, trust and safety, content moderation, spam detection and other operational functions.
    </p>
    <p>
      AI systems assist our moderation and security processes but do not replace human oversight where such
      oversight is appropriate.
    </p>

    <h3>Community Standards</h3>
    <p>
      The rules, policies and behavioural expectations published by Safeword governing the acceptable use of the
      Services and the conduct expected of Members.
    </p>

    <h3>Content</h3>
    <p>Any material made available through the Services, including but not limited to:</p>
    <ul>
      <li>photographs;</li>
      <li>videos;</li>
      <li>live streams;</li>
      <li>audio recordings;</li>
      <li>profile information;</li>
      <li>usernames;</li>
      <li>comments;</li>
      <li>posts;</li>
      <li>messages;</li>
      <li>marketplace listings;</li>
      <li>event listings;</li>
      <li>advertisements;</li>
      <li>creator content;</li>
      <li>digital downloads; and</li>
      <li>any other information uploaded, submitted, transmitted or displayed through the Services.</li>
    </ul>

    <h3>Controller</h3>
    <p>The organisation that determines the purposes and means of processing Personal Information.</p>
    <p>For the purposes of this Privacy Policy, the Controller is Hove Retail Limited.</p>

    <h3>Cookies</h3>
    <p>
      Small text files and similar technologies stored on a device to enable website functionality, improve
      security, remember preferences and assist with analytics and performance monitoring.
    </p>
    <p>
      Unless otherwise stated, references to cookies include similar technologies such as local storage, session
      storage, web beacons and pixels.
    </p>

    <h3>Creator</h3>
    <p>
      A Member who has been approved to upload, publish, promote or monetise content, products, services or events
      through the Safeword platform.
    </p>

    <h3>Data Processor</h3>
    <p>
      A person or organisation that processes Personal Information on behalf of the Controller in accordance with
      applicable data protection legislation.
    </p>
    <p>
      Examples may include trusted service providers assisting Safeword with hosting, payment processing, age
      verification, analytics, communications or technical support.
    </p>

    <h3>Hove Retail Limited</h3>
    <p>
      The company that owns and operates the Safeword platform and acts as the Data Controller for the processing of
      Personal Information described in this Privacy Policy.
    </p>

    <h3>Member</h3>
    <p>
      Any individual who registers for, accesses or uses the Safeword platform, whether as a standard member,
      creator, event organiser, marketplace seller or purchaser.
    </p>
    <p>
      References to "you" and "your" throughout this Privacy Policy refer to Members unless the context indicates
      otherwise.
    </p>

    <h3>Personal Information</h3>
    <p>
      Information relating to an identified or identifiable living individual, as defined under the UK General Data
      Protection Regulation (UK GDPR) and the Data Protection Act 2018.
    </p>
    <p>
      Personal Information may include information that directly identifies an individual or information that can
      reasonably be used in combination with other information to identify them.
    </p>
    <p>
      Where this Privacy Policy refers to "Personal Information", it has the same meaning as "Personal Data" under
      applicable UK data protection legislation.
    </p>

    <h3>Processing</h3>
    <p>Any operation performed on Personal Information, whether by automated or manual means.</p>
    <p>
      Processing includes collecting, recording, organising, storing, adapting, retrieving, consulting, using,
      disclosing, sharing, transferring, restricting, deleting, anonymising or otherwise handling Personal
      Information.
    </p>

    <h3>Safeword</h3>
    <p>
      The online platform, website, mobile applications and related Services operated by Hove Retail Limited that
      provide community, social networking, messaging, marketplace, creator, event and related services for
      consenting adults.
    </p>

    <h3>Services</h3>
    <p>All websites, applications, software, products, features, functions and services made available by Safeword from time to time.</p>

    <h3>Terms of Service</h3>
    <p>The legal agreement governing the use of the Safeword platform and Services.</p>
    <p>
      The <a routerLink="/legal/terms">Terms of Service</a> operate alongside this Privacy Policy and other platform
      policies.
    </p>

    <h3>Third-Party Service Provider</h3>
    <p>
      Any external organisation or supplier engaged by Hove Retail Limited to provide services supporting the
      operation of Safeword.
    </p>
    <p>Examples may include providers of:</p>
    <ul>
      <li>age verification;</li>
      <li>payment processing;</li>
      <li>cloud hosting;</li>
      <li>cybersecurity;</li>
      <li>analytics;</li>
      <li>communications;</li>
      <li>customer support;</li>
      <li>fraud prevention;</li>
      <li>trust and safety technologies; and</li>
      <li>other operational services.</li>
    </ul>
    <p>
      Third-Party Service Providers process Personal Information only where authorised and in accordance with
      applicable law and contractual obligations.
    </p>

    <h3>UK GDPR</h3>
    <p>
      The United Kingdom General Data Protection Regulation, as incorporated into UK law and read together with the
      Data Protection Act 2018, including any amendments, replacements or successor legislation.
    </p>

    <h3>User-Generated Content</h3>
    <p>
      Any Content created, uploaded, submitted, published, shared or otherwise made available by Members through
      the Safeword platform.
    </p>
    <p>
      User-Generated Content includes creator content, posts, comments, messages, profile information, marketplace
      listings, event listings, photographs, videos and other material contributed by Members.
    </p>

    <h3>VerifyMyAge</h3>
    <p>
      The third-party age verification service used by Safeword, where applicable, to assist in confirming that
      users meet the minimum age requirements for accessing adult content and Services.
    </p>
    <p>
      VerifyMyAge processes verification information in accordance with its own Privacy Policy and applicable legal
      requirements.
    </p>
  </section>

  <section id="uk-gdpr-compliance">
    <h2>UK GDPR Compliance</h2>

    <h3>Our Commitment to Protecting Your Privacy</h3>
    <p>Safeword is committed to protecting your privacy and handling your personal information in a lawful, fair and transparent manner.</p>
    <p>
      Hove Retail Limited ("we", "our" or "us") operates the Safeword platform and acts as the Data Controller for
      personal information collected through the Services, except where another organisation is expressly
      identified as the controller.
    </p>
    <p>We process personal information in accordance with:</p>
    <ul>
      <li>the UK General Data Protection Regulation (UK GDPR);</li>
      <li>the Data Protection Act 2018;</li>
      <li>the Privacy and Electronic Communications Regulations (PECR), where applicable;</li>
      <li>the Online Safety Act 2023, where applicable; and</li>
      <li>any other applicable data protection or privacy legislation in force within the United Kingdom.</li>
    </ul>
    <p>
      We recognise that Safeword is an adult-only platform where privacy, confidentiality and trust are fundamental
      to the safety of our members. We are committed to ensuring that personal information is collected only where
      necessary, processed lawfully, kept secure and retained only for as long as required.
    </p>

    <h3>Data Controller</h3>
    <p>For the purposes of UK data protection law, the Data Controller is:</p>
    <p><strong>Hove Retail Limited</strong></p>
    <p>The Data Controller determines how and why personal information is processed in connection with the Safeword platform.</p>
    <p>
      Where we use trusted third-party providers to deliver parts of our Services, those providers act as Data
      Processors on our behalf and may only process personal information in accordance with our documented
      instructions and applicable law.
    </p>

    <h3>Our Data Protection Principles</h3>
    <p>Whenever we process personal information, we aim to comply with the principles set out within Article 5 of the UK GDPR.</p>
    <p>Accordingly, personal information will be:</p>
    <ul>
      <li>processed lawfully, fairly and transparently;</li>
      <li>collected only for specified, explicit and legitimate purposes;</li>
      <li>limited to information that is adequate, relevant and necessary;</li>
      <li>kept accurate and, where necessary, up to date;</li>
      <li>retained only for as long as necessary;</li>
      <li>protected using appropriate technical and organisational security measures; and</li>
      <li>processed in a manner that enables us to demonstrate ongoing compliance with applicable data protection law.</li>
    </ul>

    <h3>Accountability</h3>
    <p>Hove Retail Limited maintains policies, procedures and technical safeguards designed to demonstrate compliance with UK data protection law.</p>
    <p>
      We regularly review our privacy practices, security measures and internal procedures to ensure they remain
      appropriate for the services we provide and the risks associated with processing personal information.
    </p>
    <p>Privacy considerations form part of the design, development and ongoing operation of the Safeword platform.</p>
  </section>

  <section id="information-we-collect">
    <h2>Information We Collect</h2>
    <p>
      Safeword collects information to provide, secure and improve the Services, comply with legal obligations,
      protect our community and enable members to interact safely with one another.
    </p>
    <p>The information we collect depends upon how you use the platform.</p>

    <h3>Information You Provide Directly</h3>

    <h3>Account Registration</h3>
    <p>When you create a Safeword account, we may collect:</p>
    <ul>
      <li>Your email address.</li>
      <li>Your chosen username or display name.</li>
      <li>Your date of birth.</li>
      <li>Your country of residence.</li>
      <li>Your password (stored in encrypted or hashed form).</li>
      <li>Any profile information you choose to provide.</li>
    </ul>
    <p>You are responsible for ensuring that the information you provide is accurate and kept up to date.</p>

    <h3>Profile Information</h3>
    <p>You may choose to provide additional information for your profile, including:</p>
    <ul>
      <li>Profile photographs.</li>
      <li>Biography or "About Me" information.</li>
      <li>Interests and preferences.</li>
      <li>Relationship status.</li>
      <li>Roles or identities within the BDSM and kink community.</li>
      <li>Pronouns.</li>
      <li>Event attendance.</li>
      <li>Links to external websites or social media (where permitted).</li>
      <li>Other optional profile information.</li>
    </ul>
    <p>Most profile information is optional unless otherwise stated.</p>

    <h3>Creator Information</h3>
    <p>If you apply to become a creator, seller or event organiser, we may collect additional information including:</p>
    <ul>
      <li>Identity verification information.</li>
      <li>Age verification confirmation.</li>
      <li>Payment details necessary for creator payouts.</li>
      <li>Tax information where legally required.</li>
      <li>Business information (where applicable).</li>
      <li>Bank account information for creator payments.</li>
      <li>Proof of ownership of content.</li>
      <li>Information required to investigate copyright or ownership disputes.</li>
    </ul>

    <h3>VerifyMyAge Verification</h3>
    <p>To help ensure Safeword remains an adult-only platform, we use VerifyMyAge to carry out age verification where required.</p>
    <p>Depending upon the verification method selected, VerifyMyAge may process information including:</p>
    <ul>
      <li>confirmation that you are over the age of 18;</li>
      <li>identity verification information;</li>
      <li>age verification status; and</li>
      <li>technical verification data.</li>
    </ul>
    <p>Safeword receives only the information necessary to confirm successful verification and does not receive more personal information than is required for this purpose.</p>
    <p>Please refer to VerifyMyAge's own Privacy Policy for further information about how it processes personal information.</p>

    <h3>Content You Upload</h3>
    <p>We collect information that you choose to upload, including:</p>
    <ul>
      <li>Photographs.</li>
      <li>Videos.</li>
      <li>Audio recordings.</li>
      <li>Live stream content.</li>
      <li>Written posts.</li>
      <li>Comments.</li>
      <li>Messages.</li>
      <li>Marketplace listings.</li>
      <li>Event listings.</li>
      <li>Profile updates.</li>
      <li>Blog articles.</li>
      <li>Polls.</li>
      <li>Surveys.</li>
      <li>Other user-generated content.</li>
    </ul>
    <p>When you upload content, you confirm that:</p>
    <ul>
      <li>every identifiable individual shown is aged 18 or over;</li>
      <li>every identifiable individual has provided informed consent;</li>
      <li>you have the legal right to upload the content; and</li>
      <li>the content complies with our <a routerLink="/legal/terms">Terms of Service</a> and Community Standards.</li>
    </ul>

    <h3>Communications</h3>
    <p>When you communicate through Safeword, we may collect:</p>
    <ul>
      <li>Private messages.</li>
      <li>Group conversations.</li>
      <li>Comments.</li>
      <li>Replies.</li>
      <li>Support requests.</li>
      <li>Reports submitted to moderators.</li>
      <li>Appeals.</li>
      <li>Survey responses.</li>
      <li>Feedback.</li>
    </ul>
    <p>Communications may be reviewed where necessary to investigate reports of illegal activity, protect users, comply with legal obligations or enforce our Terms of Service.</p>

    <h3>Payment Information</h3>
    <p>
      Where you purchase subscriptions, memberships, digital content or other paid services, payment transactions
      are processed by PayFasto and, where applicable, other authorised payment service providers. (Applies once
      paid subscriptions/marketplace features launch.)
    </p>
    <p>Safeword does not intentionally store your full payment card number or card security code.</p>
    <p>We may receive limited transaction information necessary to:</p>
    <ul>
      <li>confirm successful payments;</li>
      <li>process refunds;</li>
      <li>investigate fraud;</li>
      <li>prevent chargebacks;</li>
      <li>manage subscriptions; and</li>
      <li>comply with financial and legal obligations.</li>
    </ul>

    <h3>Technical Information</h3>
    <p>When you access Safeword, we automatically collect certain technical information, including:</p>
    <ul>
      <li>IP address.</li>
      <li>Browser type and version.</li>
      <li>Device type.</li>
      <li>Operating system.</li>
      <li>Language settings.</li>
      <li>Time zone.</li>
      <li>Device identifiers.</li>
      <li>Session information.</li>
      <li>Crash reports.</li>
      <li>Referring websites.</li>
      <li>Pages visited.</li>
      <li>Time spent using the platform.</li>
      <li>Features used.</li>
      <li>Cookies and similar technologies.</li>
    </ul>
    <p>This information helps us improve performance, diagnose technical issues, detect fraud and maintain platform security.</p>

    <h3>Cookies and Similar Technologies</h3>
    <p>Safeword uses cookies and similar technologies to:</p>
    <ul>
      <li>maintain user sessions;</li>
      <li>remember preferences;</li>
      <li>improve website functionality;</li>
      <li>protect against fraudulent activity;</li>
      <li>measure performance;</li>
      <li>analyse platform usage; and</li>
      <li>support security features.</li>
    </ul>
    <p>Where required by law, we will request your consent before placing non-essential cookies on your device.</p>
    <p>Further information is available within our <a routerLink="/legal/cookie-policy">Cookie Policy</a>.</p>

    <h3>Information From Third Parties</h3>
    <p>We may receive information from trusted third parties, including:</p>
    <ul>
      <li>VerifyMyAge.</li>
      <li>PayFasto.</li>
      <li>Identity verification providers.</li>
      <li>Fraud prevention providers.</li>
      <li>Payment processors.</li>
      <li>Security service providers.</li>
      <li>Law enforcement agencies where legally authorised.</li>
      <li>Regulatory authorities.</li>
      <li>Publicly available sources where relevant.</li>
    </ul>
    <p>We only use such information where we have a lawful basis to do so.</p>

    <h3>Information We Do Not Intentionally Collect</h3>
    <p>We do not knowingly collect personal information from anyone under the age of 18.</p>
    <p>
      If we become aware that an individual under the age of 18 has created an account or provided personal
      information, we will take appropriate steps to remove the account and delete the associated information,
      subject to any legal obligations requiring its preservation.
    </p>
    <p>We do not sell your personal information to advertisers or data brokers.</p>
  </section>

  <section id="how-we-use-your-information">
    <h2>How We Use Your Information</h2>
    <p>
      Safeword uses personal information only where necessary to operate the platform, provide our Services, comply
      with legal obligations, protect our members and improve the overall user experience.
    </p>
    <p>We process personal information only where we have an appropriate lawful basis under UK data protection law.</p>

    <h3>Providing the Safeword Services</h3>
    <p>We use your information to provide and operate the Services you request, including:</p>
    <ul>
      <li>Creating and managing your account.</li>
      <li>Authenticating your identity when you sign in.</li>
      <li>Displaying your profile and content according to your privacy settings.</li>
      <li>Delivering messaging, community and networking features.</li>
      <li>Enabling event listings and bookings.</li>
      <li>Operating the Safeword marketplace. (Applies once paid subscriptions/marketplace features launch.)</li>
      <li>Managing creator accounts.</li>
      <li>Processing subscriptions and digital content purchases. (Applies once paid subscriptions/marketplace features launch.)</li>
      <li>Providing customer support.</li>
      <li>Managing account preferences and settings.</li>
      <li>Maintaining account security.</li>
    </ul>
    <p>Our lawful basis for this processing is the performance of our contract with you.</p>

    <h3>Age Verification</h3>
    <p>We use your information to verify that users meet the minimum age requirement for accessing an adult platform.</p>
    <p>Where required, Safeword uses VerifyMyAge to confirm that users are aged 18 or over before allowing access to certain features or services.</p>
    <p>Our lawful basis is compliance with legal obligations and our legitimate interests in protecting children from accessing adult content.</p>

    <h3>Creator Verification</h3>
    <p>Where users apply to become creators or sell digital content through Safeword, we may process additional information to:</p>
    <ul>
      <li>Verify identity.</li>
      <li>Confirm eligibility.</li>
      <li>Prevent fraud.</li>
      <li>Meet payment processor requirements.</li>
      <li>Protect buyers and subscribers.</li>
      <li>Investigate ownership disputes.</li>
      <li>Comply with legal and regulatory obligations.</li>
    </ul>
    <p>This processing is necessary for contract performance, legal compliance and our legitimate interests.</p>

    <h3>Content Moderation</h3>
    <p>Safeword uses both AI-assisted systems and trained human moderators to help keep the platform safe.</p>
    <p>We may process information to:</p>
    <ul>
      <li>Detect illegal content.</li>
      <li>Identify child sexual abuse material (CSAM).</li>
      <li>Detect suspected non-consensual intimate imagery.</li>
      <li>Identify spam.</li>
      <li>Prevent scams and fraud.</li>
      <li>Detect fake accounts.</li>
      <li>Identify abusive behaviour.</li>
      <li>Enforce our Community Standards.</li>
      <li>Enforce our Terms of Service.</li>
      <li>Investigate reports made by users.</li>
      <li>Protect the safety of members.</li>
    </ul>
    <p>AI assists our moderation team by identifying content that may require review.</p>
    <p>Where appropriate, trained human moderators review reports and make decisions regarding warnings, content removal, account restrictions and permanent suspensions.</p>
    <p>Immediate automated action may be taken where reasonably necessary to protect users or comply with legal obligations.</p>
    <p>Our lawful basis is our legitimate interests, compliance with legal obligations and, where applicable, the protection of vital interests.</p>

    <h3>Improving the Platform</h3>
    <p>We use information to improve Safeword by:</p>
    <ul>
      <li>Analysing how features are used.</li>
      <li>Identifying technical problems.</li>
      <li>Monitoring performance.</li>
      <li>Improving reliability.</li>
      <li>Developing new features.</li>
      <li>Understanding user preferences.</li>
      <li>Conducting statistical analysis.</li>
      <li>Measuring service quality.</li>
    </ul>
    <p>Where possible, information is aggregated or anonymised before analysis.</p>

    <h3>Preventing Fraud and Abuse</h3>
    <p>We process information to:</p>
    <ul>
      <li>Detect fraudulent activity.</li>
      <li>Prevent account takeovers.</li>
      <li>Prevent payment fraud.</li>
      <li>Prevent chargeback abuse.</li>
      <li>Detect suspicious transactions.</li>
      <li>Identify fake identities.</li>
      <li>Prevent spam.</li>
      <li>Detect automated bots.</li>
      <li>Protect members from scams.</li>
      <li>Protect creators from financial abuse.</li>
    </ul>
    <p>This processing is necessary to protect both Safeword and our members.</p>

    <h3>Processing Payments</h3>
    <p>Where purchases are made through Safeword, we process information necessary to:</p>
    <ul>
      <li>Confirm successful payments.</li>
      <li>Process subscriptions.</li>
      <li>Process creator payouts.</li>
      <li>Prevent payment fraud.</li>
      <li>Investigate disputed transactions.</li>
      <li>Process refunds.</li>
      <li>Comply with financial regulations.</li>
    </ul>
    <p>(Applies once paid subscriptions/marketplace features launch.)</p>
    <p>Payment transactions are securely processed by PayFasto and, where applicable, authorised payment service providers.</p>
    <p>Safeword does not intentionally store full payment card details.</p>

    <h3>Communicating With You</h3>
    <p>We may use your information to communicate with you regarding:</p>
    <ul>
      <li>Account notifications.</li>
      <li>Security alerts.</li>
      <li>Password resets.</li>
      <li>Service announcements.</li>
      <li>Platform updates.</li>
      <li>Customer support responses.</li>
      <li>Changes to our Terms or Privacy Policy.</li>
      <li>Moderation decisions.</li>
      <li>Appeals.</li>
      <li>Transaction confirmations.</li>
      <li>Event notifications.</li>
    </ul>
    <p>These communications are necessary to provide the Services or fulfil our legal obligations.</p>

    <h3>Marketing Communications</h3>
    <p>Where permitted by law, and where you have consented where required, we may send you information about:</p>
    <ul>
      <li>New features.</li>
      <li>Product updates.</li>
      <li>Events.</li>
      <li>Promotions.</li>
      <li>Surveys.</li>
      <li>Community news.</li>
    </ul>
    <p>You may opt out of marketing communications at any time using the unsubscribe link within our emails or by updating your account preferences.</p>
    <p>Service-related communications cannot generally be opted out of where they are necessary to provide your account.</p>

    <h3>Complying With Legal Obligations</h3>
    <p>We may process personal information where necessary to:</p>
    <ul>
      <li>Comply with applicable legislation.</li>
      <li>Respond to valid legal requests.</li>
      <li>Comply with court orders.</li>
      <li>Meet regulatory requirements.</li>
      <li>Prevent financial crime.</li>
      <li>Investigate unlawful activity.</li>
      <li>Protect the rights, safety and property of Safeword, our members or others.</li>
    </ul>
    <p>Where permitted by law, we will seek to notify users before disclosing personal information unless we are legally prohibited from doing so.</p>

    <h3>Protecting the Community</h3>
    <p>Safeword is committed to maintaining a safe, respectful and lawful community.</p>
    <p>We use personal information where necessary to:</p>
    <ul>
      <li>Protect members from harm.</li>
      <li>Investigate reports of abuse.</li>
      <li>Remove unlawful content.</li>
      <li>Enforce platform rules.</li>
      <li>Protect victims of harassment.</li>
      <li>Prevent exploitation.</li>
      <li>Protect intellectual property rights.</li>
      <li>Promote trust and safety across the platform.</li>
    </ul>

    <h3>Research and Statistical Analysis</h3>
    <p>We may use aggregated or anonymised information for research, reporting and service improvement.</p>
    <p>This information does not identify individual users and may be used to understand trends, improve user experience and support business planning.</p>

    <h3>Automated Decision Making</h3>
    <p>Safeword may use automated systems, including artificial intelligence, to identify potentially unlawful or prohibited content, suspicious behaviour or security risks.</p>
    <p>
      These systems assist our moderation and security processes but do not replace human oversight for significant
      enforcement decisions, except where immediate automated action is necessary to protect users, comply with
      legal obligations or prevent serious harm.
    </p>

    <h3>Changes to How We Use Information</h3>
    <p>
      If we intend to use personal information for a purpose that is materially different from those described in
      this Privacy Policy, we will update this Policy and, where required by law, notify users or obtain additional
      consent before processing begins.
    </p>
  </section>

  <section id="legal-bases">
    <h2>Legal Bases for Processing Personal Information</h2>
    <p>
      Under the UK General Data Protection Regulation (UK GDPR), Safeword must have a lawful basis for every
      activity involving the processing of personal information.
    </p>
    <p>
      The lawful basis depends on the purpose for which the information is processed. In some circumstances, more
      than one lawful basis may apply.
    </p>
    <p>
      Where we rely on consent, you have the right to withdraw that consent at any time. Withdrawal of consent does
      not affect the lawfulness of any processing carried out before consent was withdrawn.
    </p>

    <h3>Performance of a Contract</h3>
    <p>
      We process personal information where it is necessary to enter into or perform our contract with you and
      provide the Safeword Services you have requested.
    </p>
    <p>This includes processing information to:</p>
    <ul>
      <li>Create and manage your account.</li>
      <li>Authenticate your identity when signing in.</li>
      <li>Display your profile and content according to your privacy settings.</li>
      <li>Enable messaging and community features.</li>
      <li>Operate subscriptions and memberships. (Applies once paid subscriptions/marketplace features launch.)</li>
      <li>Process purchases of digital content. (Applies once paid subscriptions/marketplace features launch.)</li>
      <li>Administer creator accounts.</li>
      <li>Manage marketplace listings. (Applies once paid subscriptions/marketplace features launch.)</li>
      <li>Manage event listings and bookings.</li>
      <li>Provide customer support.</li>
      <li>Process creator earnings and payouts.</li>
      <li>Manage account settings and preferences.</li>
    </ul>
    <p>Without processing this information, we would be unable to provide the Services requested by you.</p>

    <h3>Compliance with Legal Obligations</h3>
    <p>We process personal information where necessary to comply with legal and regulatory obligations.</p>
    <p>Examples include:</p>
    <ul>
      <li>Age verification requirements.</li>
      <li>Compliance with UK data protection legislation.</li>
      <li>Anti-fraud obligations.</li>
      <li>Anti-money laundering requirements where applicable.</li>
      <li>Financial record keeping.</li>
      <li>Tax and accounting obligations.</li>
      <li>Court orders.</li>
      <li>Law enforcement requests that we are legally required to comply with.</li>
      <li>Regulatory investigations.</li>
      <li>Protection of children and vulnerable persons.</li>
      <li>Compliance with the Online Safety Act 2023 and other applicable legislation.</li>
    </ul>
    <p>Where the law requires us to retain or disclose information, we will do so only to the extent necessary.</p>

    <h3>Legitimate Interests</h3>
    <p>
      Safeword relies on legitimate interests where processing is necessary for the operation, security and
      improvement of the platform, provided those interests are not overridden by your rights and freedoms.
    </p>
    <p>Our legitimate interests include:</p>

    <h3>Maintaining Platform Security</h3>
    <p>We process information to:</p>
    <ul>
      <li>Detect unauthorised access.</li>
      <li>Prevent hacking attempts.</li>
      <li>Protect member accounts.</li>
      <li>Identify suspicious logins.</li>
      <li>Prevent account takeovers.</li>
      <li>Monitor system integrity.</li>
    </ul>

    <h3>Fraud Prevention</h3>
    <p>We process information to:</p>
    <ul>
      <li>Detect fraudulent transactions.</li>
      <li>Prevent payment fraud.</li>
      <li>Prevent chargeback abuse.</li>
      <li>Detect fake accounts.</li>
      <li>Identify bots.</li>
      <li>Prevent scams.</li>
      <li>Protect creators and members from financial loss.</li>
    </ul>

    <h3>Trust and Safety</h3>
    <p>We process information to:</p>
    <ul>
      <li>Investigate reports submitted by users.</li>
      <li>Enforce our Community Standards.</li>
      <li>Remove unlawful content.</li>
      <li>Detect prohibited material.</li>
      <li>Prevent harassment.</li>
      <li>Protect victims of abuse.</li>
      <li>Prevent impersonation.</li>
      <li>Investigate copyright complaints.</li>
      <li>Maintain a safe online environment.</li>
    </ul>

    <h3>AI-Assisted and Human Moderation</h3>
    <p>Safeword uses a combination of artificial intelligence and trained human moderators.</p>
    <p>Personal information may be processed to:</p>
    <ul>
      <li>Identify potentially illegal content.</li>
      <li>Detect suspected child sexual abuse material (CSAM).</li>
      <li>Detect suspected non-consensual intimate images.</li>
      <li>Identify spam and automated abuse.</li>
      <li>Identify repeated policy violations.</li>
      <li>Prioritise reports requiring urgent review.</li>
    </ul>
    <p>AI systems assist our moderation processes by identifying content that may require review.</p>
    <p>Where appropriate, enforcement decisions are reviewed and determined by trained human moderators.</p>
    <p>Immediate automated action may be taken where reasonably necessary to protect users, comply with legal obligations or prevent serious harm.</p>

    <h3>Improving Our Services</h3>
    <p>We process information to:</p>
    <ul>
      <li>Improve website functionality.</li>
      <li>Develop new features.</li>
      <li>Analyse user behaviour.</li>
      <li>Improve accessibility.</li>
      <li>Monitor performance.</li>
      <li>Fix technical issues.</li>
      <li>Improve reliability.</li>
      <li>Enhance user experience.</li>
    </ul>
    <p>Where possible, information used for statistical analysis is anonymised or aggregated.</p>

    <h3>Consent</h3>
    <p>Some processing activities rely on your consent.</p>
    <p>These may include:</p>
    <ul>
      <li>Receiving marketing communications.</li>
      <li>Optional email newsletters.</li>
      <li>Participation in surveys.</li>
      <li>Optional cookies and similar technologies where consent is required.</li>
      <li>Optional profile features clearly identified as requiring consent.</li>
    </ul>
    <p>You may withdraw your consent at any time through your account settings or by contacting us.</p>
    <p>Withdrawal of consent will not affect processing carried out before consent was withdrawn.</p>

    <h3>Special Category Personal Data</h3>
    <p>
      Because Safeword is an adult-only social networking platform, users may voluntarily choose to share
      information relating to their sexual interests, sexual life, relationships or BDSM preferences.
    </p>
    <p>This information may constitute Special Category Personal Data under Article 9 UK GDPR.</p>
    <p>Safeword does not require members to provide this information unless it is necessary for specific features of the Services.</p>
    <p>Where Special Category Personal Data is processed, we will do so only where an appropriate condition under Article 9 UK GDPR applies, including where:</p>
    <ul>
      <li>the information has been manifestly made public by the user;</li>
      <li>processing is necessary for the establishment, exercise or defence of legal claims;</li>
      <li>processing is necessary for reasons of substantial public interest where permitted by law; or</li>
      <li>the user has provided explicit consent where required.</li>
    </ul>
    <p>Users remain responsible for the information they voluntarily choose to include within their public profiles.</p>

    <h3>Vital Interests</h3>
    <p>In exceptional circumstances, we may process personal information where necessary to protect the life or physical safety of an individual.</p>
    <p>Examples may include:</p>
    <ul>
      <li>responding to credible threats of serious harm;</li>
      <li>protecting users from immediate danger;</li>
      <li>assisting emergency services where appropriate; or</li>
      <li>complying with safeguarding obligations.</li>
    </ul>
    <p>This lawful basis will only be relied upon where no other lawful basis is more appropriate.</p>

    <h3>Legal Claims</h3>
    <p>We may process personal information where necessary to establish, exercise or defend legal claims.</p>
    <p>This may include:</p>
    <ul>
      <li>responding to litigation;</li>
      <li>protecting Safeword's legal rights;</li>
      <li>defending legal proceedings;</li>
      <li>investigating contractual disputes;</li>
      <li>responding to intellectual property claims; or</li>
      <li>complying with court procedures.</li>
    </ul>

    <h3>Reviewing Our Lawful Bases</h3>
    <p>
      We regularly review the lawful bases on which we process personal information to ensure that our processing
      remains lawful, fair, transparent and proportionate.
    </p>
    <p>
      If the purpose for processing changes materially, we will update this Privacy Policy and, where required by
      law, notify users or obtain additional consent before carrying out the new processing activity.
    </p>
  </section>

  <section id="sharing-your-information">
    <h2>Sharing Your Information</h2>
    <p>
      Safeword respects your privacy and does not sell, rent or trade your personal information to advertisers, data
      brokers or other third parties for their own marketing purposes.
    </p>
    <p>
      We only share personal information where it is necessary to provide our Services, comply with legal
      obligations, protect our community or where you have instructed or authorised us to do so.
    </p>
    <p>
      Whenever we share personal information with third parties, we take reasonable steps to ensure that
      appropriate contractual, technical and organisational safeguards are in place to protect your information and
      comply with applicable data protection laws.
    </p>

    <h3>VerifyMyAge</h3>
    <p>Safeword uses VerifyMyAge to help verify that users meet the minimum age requirements for accessing an adult platform.</p>
    <p>
      Where age verification is required, limited personal information may be shared with VerifyMyAge solely for
      the purpose of verifying your age or identity.
    </p>
    <p>VerifyMyAge acts as an independent service provider and processes personal information in accordance with its own Privacy Policy.</p>
    <p>
      Safeword receives only the information necessary to confirm whether verification has been successfully
      completed and does not request or receive more information than is reasonably required for this purpose.
    </p>

    <h3>PayFasto</h3>
    <p>Safeword uses PayFasto to securely process payments made through the platform.</p>
    <p>Where you purchase subscriptions, digital content, memberships or other paid services, payment-related information is securely transmitted to PayFasto.</p>
    <p>Safeword does not intentionally collect or store your complete payment card number or card security code.</p>
    <p>We may receive limited transaction information from PayFasto, including payment confirmation, subscription status, transaction references and fraud alerts where necessary to:</p>
    <ul>
      <li>process payments;</li>
      <li>administer subscriptions;</li>
      <li>issue refunds;</li>
      <li>investigate disputed transactions;</li>
      <li>prevent fraud;</li>
      <li>detect chargeback abuse; and</li>
      <li>comply with financial and legal obligations.</li>
    </ul>
    <p>PayFasto processes personal information in accordance with its own Privacy Policy and applicable payment industry standards.</p>
    <p>(Applies once paid subscriptions/marketplace features launch.)</p>

    <h3>Cloud Hosting Providers</h3>
    <p>Safeword uses trusted cloud hosting providers and infrastructure partners to host and operate the platform.</p>
    <p>
      These providers may process personal information solely for the purpose of providing secure hosting, storage,
      networking, backups, disaster recovery and technical infrastructure.
    </p>
    <p>
      Our hosting providers are contractually required to process personal information only on our documented
      instructions and to implement appropriate security measures.
    </p>

    <h3>Email and Communication Providers</h3>
    <p>We use trusted third-party communication providers to deliver:</p>
    <ul>
      <li>account verification emails;</li>
      <li>password reset emails;</li>
      <li>security notifications;</li>
      <li>transaction confirmations;</li>
      <li>support communications;</li>
      <li>moderation notifications;</li>
      <li>service announcements; and</li>
      <li>marketing communications where permitted.</li>
    </ul>
    <p>
      These providers process personal information only for the purpose of delivering communications on our behalf
      and are not permitted to use your information for their own marketing or commercial purposes.
    </p>

    <h3>Analytics Providers</h3>
    <p>Safeword may use trusted analytics providers to help us understand how members use the platform and improve our Services.</p>
    <p>Analytics information may include:</p>
    <ul>
      <li>pages viewed;</li>
      <li>features used;</li>
      <li>session duration;</li>
      <li>browser type;</li>
      <li>operating system;</li>
      <li>device type;</li>
      <li>approximate location derived from IP address;</li>
      <li>technical performance information; and</li>
      <li>anonymous usage statistics.</li>
    </ul>
    <p>Where possible, analytics data is aggregated, pseudonymised or anonymised.</p>
    <p>We do not use analytics providers to sell personal information or build advertising profiles about our members.</p>

    <h3>Moderation and Trust &amp; Safety Providers</h3>
    <p>Safeword uses a combination of internal moderation systems, AI-assisted technologies and trained human moderators to help protect the platform.</p>
    <p>Where appropriate, trusted specialist service providers may assist with:</p>
    <ul>
      <li>detecting spam;</li>
      <li>fraud prevention;</li>
      <li>malware detection;</li>
      <li>image hashing;</li>
      <li>illegal content detection;</li>
      <li>platform security;</li>
      <li>abuse prevention; and</li>
      <li>trust and safety operations.</li>
    </ul>
    <p>
      Where external moderation technologies are used, we aim to minimise the amount of personal information shared
      and require providers to maintain appropriate confidentiality, security and data protection standards.
    </p>

    <h3>Law Enforcement and Regulatory Authorities</h3>
    <p>We may disclose personal information where we are legally required or authorised to do so.</p>
    <p>This may include disclosures to:</p>
    <ul>
      <li>UK law enforcement agencies;</li>
      <li>courts and tribunals;</li>
      <li>regulatory authorities;</li>
      <li>government departments;</li>
      <li>safeguarding organisations; or</li>
      <li>other competent authorities.</li>
    </ul>
    <p>We may also disclose information where we reasonably believe it is necessary to:</p>
    <ul>
      <li>comply with applicable law;</li>
      <li>comply with a valid court order or other legally binding request;</li>
      <li>investigate suspected criminal activity;</li>
      <li>prevent fraud;</li>
      <li>protect children or vulnerable individuals;</li>
      <li>prevent serious harm;</li>
      <li>enforce our legal rights;</li>
      <li>defend legal claims; or</li>
      <li>protect the rights, property or safety of Safeword, our members or others.</li>
    </ul>
    <p>Where legally permitted, we will seek to notify affected users before disclosing personal information unless doing so would be unlawful or would prejudice an investigation.</p>

    <h3>Professional Advisers</h3>
    <p>We may share personal information with our professional advisers where necessary for the operation of our business.</p>
    <p>This may include:</p>
    <ul>
      <li>solicitors;</li>
      <li>barristers;</li>
      <li>accountants;</li>
      <li>auditors;</li>
      <li>insurers;</li>
      <li>compliance consultants;</li>
      <li>cybersecurity advisers;</li>
      <li>payment compliance specialists; and</li>
      <li>other professional advisers engaged by Hove Retail Limited.</li>
    </ul>
    <p>Professional advisers are required to maintain confidentiality and may only use the information for the purposes for which it has been disclosed.</p>

    <h3>Business Transactions</h3>
    <p>
      If Hove Retail Limited undergoes a merger, acquisition, investment, restructuring, sale of assets or other
      corporate transaction, personal information may be transferred as part of that transaction where permitted by
      law.
    </p>
    <p>
      Should such a transfer occur, we will take reasonable steps to ensure that your personal information continues
      to be protected in accordance with this Privacy Policy or that you are notified of any material changes where
      required by law.
    </p>

    <h3>We Never Sell Your Personal Information</h3>
    <p>Safeword does not sell your personal information to advertisers, data brokers or third parties for their own marketing purposes.</p>
    <p>
      We believe that privacy is fundamental to maintaining trust within our community and are committed to
      protecting the confidentiality of our members' personal information.
    </p>
  </section>

  <section id="international-transfers">
    <h2>International Data Transfers</h2>
    <p>Safeword is operated by Hove Retail Limited in the United Kingdom.</p>
    <p>
      We aim, wherever reasonably practicable, to process and store personal information within the United Kingdom
      or in jurisdictions that provide an adequate level of protection for personal information under UK data
      protection law.
    </p>
    <p>
      However, because we use carefully selected third-party service providers to operate the Safeword platform,
      certain personal information may be transferred to, stored in or accessed from countries outside the United
      Kingdom.
    </p>
    <p>
      Where this occurs, we take appropriate steps to ensure that your personal information continues to receive a
      level of protection that is substantially equivalent to that required under the UK General Data Protection
      Regulation (UK GDPR).
    </p>

    <h3>Why International Transfers May Occur</h3>
    <p>International transfers may occur where we use trusted service providers for services such as:</p>
    <ul>
      <li>cloud hosting and infrastructure;</li>
      <li>secure data storage and backups;</li>
      <li>email delivery;</li>
      <li>customer support systems;</li>
      <li>fraud prevention;</li>
      <li>cybersecurity services;</li>
      <li>content moderation technologies;</li>
      <li>analytics services;</li>
      <li>payment processing;</li>
      <li>age verification;</li>
      <li>disaster recovery;</li>
      <li>software development and maintenance; and</li>
      <li>other business-critical technical services.</li>
    </ul>
    <p>We carefully assess providers before engaging them and seek to ensure they implement appropriate technical and organisational security measures.</p>

    <h3>Countries Where Data May Be Processed</h3>
    <p>Depending on the service providers we use from time to time, personal information may be processed in:</p>
    <ul>
      <li>the United Kingdom;</li>
      <li>countries recognised by the UK Government as providing an adequate level of protection for personal data; or</li>
      <li>other countries where appropriate legal safeguards have been implemented.</li>
    </ul>
    <p>The countries in which processing takes place may change as our business, infrastructure and service providers evolve.</p>

    <h3>Safeguards for International Transfers</h3>
    <p>
      Where personal information is transferred outside the United Kingdom to a country that has not been recognised
      as providing an adequate level of protection, Safeword will implement appropriate safeguards as required by UK
      data protection law.
    </p>
    <p>These safeguards may include:</p>
    <ul>
      <li>the UK International Data Transfer Agreement (IDTA);</li>
      <li>the UK Addendum to the European Commission's Standard Contractual Clauses (SCCs);</li>
      <li>contractual obligations requiring service providers to protect personal information;</li>
      <li>technical security measures such as encryption and access controls;</li>
      <li>organisational measures designed to protect confidentiality; and</li>
      <li>transfer risk assessments where appropriate.</li>
    </ul>
    <p>We will only transfer personal information internationally where we are satisfied that appropriate protections are in place.</p>

    <h3>Adequacy Decisions</h3>
    <p>
      Where personal information is transferred to a country that has been recognised by the United Kingdom as
      providing an adequate level of protection for personal information, those transfers may take place without
      additional transfer mechanisms.
    </p>
    <p>We monitor changes to UK adequacy regulations and review our international transfer arrangements where necessary.</p>

    <h3>Security of International Transfers</h3>
    <p>Regardless of where your personal information is processed, we expect all service providers handling personal information on our behalf to:</p>
    <ul>
      <li>maintain appropriate security standards;</li>
      <li>restrict access to authorised personnel;</li>
      <li>process information only on our documented instructions;</li>
      <li>maintain appropriate confidentiality obligations;</li>
      <li>implement suitable technical and organisational measures;</li>
      <li>notify us of security incidents where required; and</li>
      <li>comply with applicable contractual and legal obligations relating to data protection.</li>
    </ul>
    <p>Where appropriate, we conduct due diligence and periodic reviews of service providers to help ensure they continue to meet our security and privacy expectations.</p>

    <h3>Remote Access</h3>
    <p>
      In certain circumstances, authorised personnel or contracted service providers may securely access personal
      information from outside the United Kingdom in order to:
    </p>
    <ul>
      <li>maintain platform infrastructure;</li>
      <li>investigate technical incidents;</li>
      <li>provide customer support;</li>
      <li>respond to security incidents;</li>
      <li>maintain software;</li>
      <li>investigate reports of unlawful activity; or</li>
      <li>provide specialist professional services.</li>
    </ul>
    <p>Any such access is subject to appropriate security controls, confidentiality obligations and access restrictions.</p>

    <h3>Your Rights Regarding International Transfers</h3>
    <p>Where your personal information is transferred internationally, you continue to benefit from the protections provided under applicable UK data protection law.</p>
    <p>You may contact us at any time if you would like further information about:</p>
    <ul>
      <li>the countries to which your information may be transferred;</li>
      <li>the safeguards we rely upon;</li>
      <li>the categories of service providers involved in international processing; or</li>
      <li>how we protect your personal information when international transfers occur.</li>
    </ul>
    <p>Where required by law, we will provide additional information about international transfers upon request.</p>

    <h3>Changes to Our International Transfers</h3>
    <p>As Safeword grows and our technology evolves, we may engage additional service providers located in different jurisdictions.</p>
    <p>If this results in material changes to how personal information is transferred internationally, we will update this Privacy Policy accordingly and, where required by law, notify users of those changes.</p>
  </section>

  <section id="security-of-your-information">
    <h2>Security of Your Information</h2>
    <p>Safeword recognises that protecting the privacy and security of our members is fundamental to maintaining a safe and trusted community.</p>
    <p>
      Hove Retail Limited implements appropriate technical, organisational and administrative security measures
      designed to protect personal information against accidental or unlawful destruction, loss, alteration,
      unauthorised disclosure or unauthorised access.
    </p>
    <p>
      Although no online service can guarantee absolute security, we continually review and improve our security
      measures to reduce risk and help protect the confidentiality, integrity and availability of personal
      information.
    </p>

    <h3>Security by Design</h3>
    <p>Privacy and security are considered throughout the design, development and operation of the Safeword platform.</p>
    <p>
      Where reasonably practicable, we apply the principles of privacy by design and privacy by default when
      introducing new features, technologies or services.
    </p>
    <p>
      We regularly review our systems, procedures and operational practices to help ensure they remain appropriate
      for the nature of the Services we provide and the risks associated with processing personal information.
    </p>

    <h3>Encryption</h3>
    <p>Safeword uses industry-standard encryption technologies to help protect personal information.</p>
    <p>Where appropriate, this includes:</p>
    <ul>
      <li>encryption of data transmitted between your device and our platform using HTTPS/TLS;</li>
      <li>encryption of sensitive information while in transit;</li>
      <li>encryption of data at rest where appropriate and reasonably practicable;</li>
      <li>encrypted backups where supported by our infrastructure; and</li>
      <li>secure encryption key management.</li>
    </ul>
    <p>Encryption technologies may evolve over time as industry standards develop.</p>

    <h3>Password Security</h3>
    <p>Passwords are an important part of protecting your account.</p>
    <p>Safeword does not intentionally store passwords in plain text.</p>
    <p>
      Passwords are stored using secure cryptographic hashing techniques and appropriate security controls designed
      to reduce the risk of unauthorised disclosure.
    </p>
    <p>Members are responsible for:</p>
    <ul>
      <li>choosing a strong password;</li>
      <li>keeping their password confidential;</li>
      <li>not sharing login credentials with others;</li>
      <li>changing passwords if they suspect compromise; and</li>
      <li>notifying Safeword immediately if they believe their account has been accessed without authorisation.</li>
    </ul>
    <p>Where available, we encourage members to enable additional account security features.</p>

    <h3>Access Controls</h3>
    <p>Access to personal information is restricted to authorised personnel who require access in order to perform their duties.</p>
    <p>We apply role-based access controls designed to ensure that individuals only have access to the information necessary for their responsibilities.</p>
    <p>Access permissions are reviewed periodically and may be modified or revoked where no longer required.</p>

    <h3>Staff Confidentiality</h3>
    <p>Employees, moderators, contractors and authorised service providers with access to personal information are subject to appropriate confidentiality obligations.</p>
    <p>Access to personal information is granted only where necessary to perform legitimate business, operational, security or legal functions.</p>
    <p>Personnel are expected to handle personal information responsibly and in accordance with our internal policies and applicable law.</p>

    <h3>Platform Monitoring</h3>
    <p>To help protect the integrity and security of the Services, Safeword may monitor platform activity for signs of:</p>
    <ul>
      <li>unauthorised access;</li>
      <li>suspicious account activity;</li>
      <li>attempted fraud;</li>
      <li>spam;</li>
      <li>automated abuse;</li>
      <li>malware;</li>
      <li>denial-of-service attacks;</li>
      <li>compromised accounts;</li>
      <li>unusual login activity; and</li>
      <li>other security threats.</li>
    </ul>
    <p>Monitoring is carried out only where appropriate and proportionate to protect the platform, our members and our legal obligations.</p>

    <h3>AI-Assisted and Human Security Monitoring</h3>
    <p>Safeword uses both automated technologies and trained personnel to identify potential security risks.</p>
    <p>These systems may help detect:</p>
    <ul>
      <li>compromised accounts;</li>
      <li>fraudulent transactions;</li>
      <li>suspicious login behaviour;</li>
      <li>bot activity;</li>
      <li>credential stuffing attacks;</li>
      <li>malicious software;</li>
      <li>account takeover attempts;</li>
      <li>abusive behaviour;</li>
      <li>coordinated spam campaigns; and</li>
      <li>other threats to the platform.</li>
    </ul>
    <p>AI assists in identifying patterns requiring attention, while appropriately authorised personnel investigate and determine suitable responses where necessary.</p>

    <h3>Payment Security</h3>
    <p>Payments made through Safeword are processed securely by PayFasto and other authorised payment service providers where applicable.</p>
    <p>Safeword does not intentionally store complete payment card numbers or card security codes.</p>
    <p>
      Payment processing is carried out using industry-standard security measures appropriate to the services
      provided. (Applies once paid subscriptions/marketplace features launch.)
    </p>

    <h3>Third-Party Service Providers</h3>
    <p>
      Where trusted third-party providers process personal information on our behalf, we seek to ensure they
      implement appropriate security measures consistent with applicable legal and contractual obligations.
    </p>
    <p>Where appropriate, service providers may be required to:</p>
    <ul>
      <li>maintain recognised security standards;</li>
      <li>restrict access to authorised personnel;</li>
      <li>implement access controls;</li>
      <li>maintain confidentiality;</li>
      <li>notify us of relevant security incidents; and</li>
      <li>process information only in accordance with our documented instructions.</li>
    </ul>

    <h3>System Maintenance</h3>
    <p>We regularly maintain our systems and infrastructure to help reduce security risks.</p>
    <p>This may include:</p>
    <ul>
      <li>applying software updates;</li>
      <li>installing security patches;</li>
      <li>reviewing system configurations;</li>
      <li>monitoring vulnerabilities;</li>
      <li>testing security controls;</li>
      <li>reviewing access permissions; and</li>
      <li>improving operational resilience.</li>
    </ul>
    <p>Maintenance schedules may change as technology and security risks evolve.</p>

    <h3>Data Backups and Business Continuity</h3>
    <p>
      To support the continuity of our Services and help protect against accidental loss of information, Safeword
      maintains backup and recovery procedures appropriate to our operational requirements.
    </p>
    <p>Backups may be encrypted where appropriate and stored securely in accordance with our retention and security policies.</p>
    <p>Backup data is retained only for as long as necessary to support recovery and business continuity objectives.</p>

    <h3>Incident Response</h3>
    <p>Safeword maintains procedures for responding to suspected security incidents affecting personal information.</p>
    <p>Where appropriate, our response may include:</p>
    <ul>
      <li>investigating the incident;</li>
      <li>containing the risk;</li>
      <li>preserving relevant evidence;</li>
      <li>restoring affected systems;</li>
      <li>notifying relevant service providers;</li>
      <li>notifying regulatory authorities where required by law;</li>
      <li>notifying affected users where legally required; and</li>
      <li>reviewing procedures to reduce the likelihood of similar incidents occurring in the future.</li>
    </ul>
    <p>
      Where a personal data breach is likely to result in a risk to your rights and freedoms, we will notify the
      Information Commissioner's Office without undue delay and, where feasible, within 72 hours of becoming aware
      of it, in accordance with Article 33 UK GDPR.
    </p>

    <h3>Your Role in Protecting Your Information</h3>
    <p>While we work to protect your information, security is a shared responsibility.</p>
    <p>We encourage members to:</p>
    <ul>
      <li>use strong, unique passwords;</li>
      <li>keep login credentials confidential;</li>
      <li>sign out from shared devices;</li>
      <li>keep devices and browsers updated;</li>
      <li>be alert to phishing attempts;</li>
      <li>avoid sharing verification codes;</li>
      <li>report suspected account compromise immediately; and</li>
      <li>notify us of any suspected security vulnerabilities or unauthorised activity.</li>
    </ul>

    <h3>Continuous Improvement</h3>
    <p>
      Cybersecurity threats continue to evolve. Safeword regularly reviews its technical, organisational and
      operational security measures and may introduce additional safeguards, technologies and procedures, in line
      with recognised industry standards and applicable legal requirements, as appropriate to help protect personal
      information and maintain the security of the Services.
    </p>
  </section>

  <section id="data-retention">
    <h2>Data Retention</h2>
    <p>
      Safeword retains personal information only for as long as it is reasonably necessary to fulfil the purposes
      for which it was collected, to comply with our legal obligations, to resolve disputes, to enforce our
      agreements, to protect our community, and to maintain the security and integrity of the Services.
    </p>
    <p>
      Retention periods may vary depending on the type of information, the purpose for which it was collected, legal
      or regulatory requirements, and the legitimate needs of Hove Retail Limited.
    </p>
    <p>Where personal information is no longer required, we will securely delete, anonymise or otherwise dispose of it in accordance with our data retention procedures.</p>

    <h3>Active Accounts</h3>
    <p>While your account remains active, we retain personal information necessary to:</p>
    <ul>
      <li>provide the Safeword Services;</li>
      <li>maintain your account and profile;</li>
      <li>display your content in accordance with your privacy settings;</li>
      <li>process subscriptions and purchases;</li>
      <li>enable messaging and community features;</li>
      <li>administer creator accounts;</li>
      <li>provide customer support;</li>
      <li>maintain platform security; and</li>
      <li>comply with legal and regulatory obligations.</li>
    </ul>

    <h3>Inactive Accounts</h3>
    <p>If an account becomes inactive, we may retain account information for a reasonable period to:</p>
    <ul>
      <li>allow users to reactivate their account;</li>
      <li>investigate complaints;</li>
      <li>detect fraud;</li>
      <li>maintain system integrity;</li>
      <li>comply with legal obligations; and</li>
      <li>preserve records required for legitimate business purposes.</li>
    </ul>
    <p>Inactive accounts may eventually be anonymised or deleted where continued retention is no longer necessary.</p>
    <p>
      An account is treated as inactive after 12 months without login. We will send a reminder notice, and if the
      account remains inactive, associated personal data will be anonymised or deleted after 24 months of total
      inactivity.
    </p>

    <h3>Account Deletion</h3>
    <p>Members may request deletion of their account at any time through the Safeword platform or by contacting us.</p>
    <p>Following a deletion request, we will begin our account deletion process.</p>
    <p>Some information may remain temporarily while:</p>
    <ul>
      <li>verifying the request;</li>
      <li>completing the deletion process;</li>
      <li>resolving outstanding disputes;</li>
      <li>processing refunds;</li>
      <li>complying with legal obligations; or</li>
      <li>protecting the rights of Safeword and other members.</li>
    </ul>
    <p>Where information is no longer required, it will be securely deleted or anonymised.</p>
    <p>Certain information may continue to be retained where required by law or where necessary to establish, exercise or defend legal claims.</p>
    <p>
      Most account data is deleted within 30 days of a verified deletion request, except for the categories noted
      elsewhere in this section (e.g. payment/financial records, security logs, legal holds) which are retained for
      their own specified periods.
    </p>

    <h3>VerifyMyAge Records</h3>
    <p>Safeword uses VerifyMyAge to assist with age verification.</p>
    <p>We retain only the information necessary to demonstrate that age verification has been completed and to comply with applicable legal and regulatory obligations.</p>
    <p>
      Verification information is retained only for as long as reasonably necessary and in accordance with our
      legal obligations and contractual arrangements with VerifyMyAge.
    </p>
    <p>
      Consistent with standard UK age-assurance practice, the underlying verification evidence (e.g. ID documents,
      facial images) is expected to be processed and deleted by VerifyMyAge shortly after the check is completed
      (commonly within 24–72 hours) and is not retained by Safeword. Safeword itself retains only the verification
      outcome (e.g. "age verified: yes," date and method) for as long as the account remains active, plus a limited
      compliance window afterwards, to demonstrate compliance to regulators.
    </p>

    <h3>Creator Verification Records</h3>
    <p>Where creators complete identity or eligibility verification, Safeword may retain verification records for the purposes of:</p>
    <ul>
      <li>fraud prevention;</li>
      <li>payment compliance;</li>
      <li>dispute resolution;</li>
      <li>protecting buyers and subscribers;</li>
      <li>complying with legal obligations;</li>
      <li>responding to regulatory enquiries; and</li>
      <li>maintaining the integrity of the platform.</li>
    </ul>
    <p>Verification records are retained only for as long as reasonably necessary or as required by applicable law.</p>
    <p>
      Creator verification records are retained for the duration the creator account remains active, plus 6 years
      afterwards to align with the financial record-keeping period (creator verification is closely tied to
      payment/tax compliance).
    </p>

    <h3>User Generated Content</h3>
    <p>Content uploaded by members may be retained while:</p>
    <ul>
      <li>the content remains published;</li>
      <li>the member maintains an active account;</li>
      <li>it is required to investigate reports;</li>
      <li>it is required to resolve disputes;</li>
      <li>it is required to comply with legal obligations; or</li>
      <li>it is required to protect the rights of Safeword or other users.</li>
    </ul>
    <p>Where content is deleted, copies may remain within secure backup systems for a limited period before permanent deletion.</p>
    <p>
      Deleted content is removed from live systems immediately and purged from backups within 30–90 days, in line
      with our backup retention cycle, unless retained longer under Moderation Records or Legal Holds.
    </p>

    <h3>Messages and Communications</h3>
    <p>Private messages, support requests, moderation correspondence and other communications may be retained for as long as reasonably necessary to:</p>
    <ul>
      <li>provide the Services;</li>
      <li>investigate complaints;</li>
      <li>resolve disputes;</li>
      <li>prevent fraud;</li>
      <li>enforce our Terms of Service;</li>
      <li>investigate unlawful activity; and</li>
      <li>comply with legal obligations.</li>
    </ul>
    <p>Where appropriate, communications may be securely deleted or anonymised when no longer required.</p>
    <p>
      Messages are retained for as long as the account is active, and deleted or anonymised within 30–90 days of
      account deletion, unless a longer period applies under Moderation Records or Legal Holds.
    </p>

    <h3>Payment Information</h3>
    <p>Payment transactions are processed by PayFasto.</p>
    <p>Safeword does not intentionally retain complete payment card details.</p>
    <p>We may retain limited payment-related information, including transaction references, invoices, refunds and subscription records where necessary to:</p>
    <ul>
      <li>administer accounts;</li>
      <li>process refunds;</li>
      <li>investigate disputed transactions;</li>
      <li>comply with accounting and tax obligations;</li>
      <li>prevent fraud; and</li>
      <li>meet legal or regulatory requirements.</li>
    </ul>
    <p>(Applies once paid subscriptions/marketplace features launch.)</p>
    <p>
      Transaction records, invoices and subscription records are retained for 6 years from the date of the
      transaction, in line with HMRC record-keeping requirements (VAT Notice 700/21), the Companies Act 2006
      (s.388), and the Limitation Act 1980. Safeword does not store full card numbers; these are held by PayFasto in
      accordance with its own retention schedule.
    </p>

    <h3>Moderation Records</h3>
    <p>To maintain the safety and integrity of the platform, Safeword may retain records relating to:</p>
    <ul>
      <li>reported content;</li>
      <li>reported accounts;</li>
      <li>moderation decisions;</li>
      <li>appeals;</li>
      <li>warnings;</li>
      <li>suspensions;</li>
      <li>permanent bans;</li>
      <li>investigations; and</li>
      <li>enforcement actions.</li>
    </ul>
    <p>These records help ensure consistency, fairness, transparency and compliance with legal obligations.</p>
    <p>
      Moderation records are retained for 24 months following resolution, or for as long as the associated account
      remains active where needed to identify repeat violations or respond to safeguarding concerns.
    </p>

    <h3>Security Logs</h3>
    <p>We may retain security-related information including:</p>
    <ul>
      <li>login history;</li>
      <li>IP address logs;</li>
      <li>device information;</li>
      <li>fraud detection records;</li>
      <li>security alerts;</li>
      <li>authentication records;</li>
      <li>audit logs; and</li>
      <li>system activity records.</li>
    </ul>
    <p>Security logs are retained only for as long as reasonably necessary to maintain the security of the platform, investigate incidents and comply with legal obligations.</p>
    <p>
      Security logs are retained for a minimum of 6 months and up to 12 months, in line with NCSC guidance
      ("Introduction to Logging for Security Purposes"), which recommends at least 6 months given typical breach
      dwell times.
    </p>

    <h3>Backups</h3>
    <p>Personal information may continue to exist within encrypted backup systems for a limited period following deletion from our live systems.</p>
    <p>Backup data is retained solely for disaster recovery, business continuity and security purposes.</p>
    <p>Backups are securely managed and automatically expire in accordance with our internal retention procedures.</p>
    <p>Rolling backups are retained for 30–90 days before automatic expiry.</p>

    <h3>Legal Holds</h3>
    <p>Where required by law, Safeword may retain personal information beyond the normal retention period.</p>
    <p>This may occur where information is required in connection with:</p>
    <ul>
      <li>court proceedings;</li>
      <li>legal claims;</li>
      <li>law enforcement investigations;</li>
      <li>regulatory investigations;</li>
      <li>fraud investigations;</li>
      <li>safeguarding concerns; or</li>
      <li>other legal obligations.</li>
    </ul>
    <p>Information subject to a legal hold will be retained only for as long as necessary for those purposes.</p>

    <h3>Anonymisation</h3>
    <p>
      Where it is no longer necessary to retain information in an identifiable form, Safeword may anonymise or
      aggregate information so that it can no longer be linked to an identifiable individual.
    </p>
    <p>Anonymised information may be retained for statistical analysis, service improvement, business planning and research.</p>

    <h3>Secure Disposal</h3>
    <p>
      When personal information reaches the end of its retention period and is no longer required, Safeword will
      take reasonable steps to securely delete, destroy or anonymise the information using methods appropriate to
      the nature of the data and the systems in which it is stored.
    </p>

    <h3>Review of Retention Periods</h3>
    <p>
      Safeword regularly reviews its retention practices to ensure that personal information is not kept for longer
      than necessary and that retention periods remain appropriate, proportionate and compliant with applicable
      legal and regulatory requirements, updating them from time to time to reflect changes in legislation,
      operational requirements or industry best practice.
    </p>
  </section>

  <section id="your-rights">
    <h2>Your Rights Under UK GDPR</h2>
    <p>Safeword respects your rights under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>
    <p>Subject to applicable law, you have a number of rights regarding the personal information that Hove Retail Limited processes about you.</p>
    <p>
      These rights are not absolute and may be subject to certain legal exemptions or limitations. Where we are
      unable to comply with a request, we will explain the reasons unless we are legally prevented from doing so.
    </p>
    <p>You may exercise your rights by contacting us using the contact details provided in this Privacy Policy.</p>
    <p>We may ask you to verify your identity before responding to your request in order to protect your personal information and prevent unauthorised access.</p>

    <h3>Right of Access</h3>
    <p>You have the right to request confirmation as to whether we process your personal information.</p>
    <p>Where we do, you may request:</p>
    <ul>
      <li>confirmation that your personal information is being processed;</li>
      <li>a copy of the personal information we hold about you;</li>
      <li>details of the purposes for which it is processed;</li>
      <li>the categories of information we hold;</li>
      <li>the recipients or categories of recipients with whom it has been shared;</li>
      <li>the expected retention period, where possible;</li>
      <li>information about your legal rights;</li>
      <li>information about international transfers; and</li>
      <li>information about the source of the data where it was not collected directly from you.</li>
    </ul>
    <p>Unless an exemption applies, we will provide this information without undue delay and generally within one month of receiving your request.</p>

    <h3>Right to Rectification</h3>
    <p>You have the right to request that inaccurate or incomplete personal information be corrected.</p>
    <p>Many profile details can be updated directly through your Safeword account settings.</p>
    <p>Where information cannot be updated by you directly, you may ask us to correct or complete it.</p>
    <p>We may request evidence where necessary to verify the accuracy of the requested changes.</p>

    <h3>Right to Erasure ("Right to be Forgotten")</h3>
    <p>You may request that we delete your personal information where:</p>
    <ul>
      <li>it is no longer necessary for the purpose for which it was collected;</li>
      <li>you withdraw consent and no other lawful basis applies;</li>
      <li>you successfully object to the processing;</li>
      <li>the information has been unlawfully processed; or</li>
      <li>we are legally required to erase it.</li>
    </ul>
    <p>This right is not absolute.</p>
    <p>We may retain certain information where necessary to:</p>
    <ul>
      <li>comply with legal obligations;</li>
      <li>investigate fraud;</li>
      <li>protect other users;</li>
      <li>establish, exercise or defend legal claims;</li>
      <li>complete financial or accounting obligations;</li>
      <li>maintain platform security; or</li>
      <li>comply with regulatory requirements.</li>
    </ul>

    <h3>Right to Restrict Processing</h3>
    <p>You may ask us to restrict the processing of your personal information where:</p>
    <ul>
      <li>you contest its accuracy;</li>
      <li>the processing is unlawful but you do not want the information deleted;</li>
      <li>we no longer require the information but you need it for legal claims; or</li>
      <li>you have objected to processing while we consider your request.</li>
    </ul>
    <p>During any period of restriction, we will continue to store the information but will only process it where permitted by law.</p>

    <h3>Right to Object</h3>
    <p>You have the right to object to certain processing carried out on the basis of our legitimate interests.</p>
    <p>If you object, we will carefully consider your request.</p>
    <p>We may continue processing where we have compelling legitimate grounds that override your interests, rights and freedoms or where processing is necessary for legal claims.</p>
    <p>You also have the right to object to direct marketing at any time.</p>
    <p>If you object to direct marketing, we will stop sending marketing communications unless you subsequently choose to opt in again.</p>

    <h3>Right to Data Portability</h3>
    <p>
      Where processing is based on your consent or on the performance of a contract and is carried out by automated
      means, you may request a copy of certain personal information in a structured, commonly used and
      machine-readable format.
    </p>
    <p>Where technically feasible, and where permitted by law, you may also request that the information be transmitted directly to another service provider.</p>

    <h3>Right to Withdraw Consent</h3>
    <p>Where we rely on your consent to process personal information, you may withdraw that consent at any time.</p>
    <p>Withdrawal of consent will not affect the lawfulness of processing carried out before consent was withdrawn.</p>
    <p>Certain Services may no longer be available where consent is necessary for their operation.</p>

    <h3>Rights Relating to Automated Decision-Making</h3>
    <p>
      Safeword uses automated technologies, including AI-assisted systems, to help identify potentially unlawful or
      prohibited content, suspicious activity and security risks.
    </p>
    <p>These technologies assist our moderation and security processes but are generally used alongside human oversight for significant enforcement decisions.</p>
    <p>Where applicable under UK data protection law, you may request human review of decisions that significantly affect you where those decisions have been made solely by automated means.</p>

    <h3>Right to Lodge a Complaint</h3>
    <p>
      If you believe that we have not handled your personal information in accordance with applicable data
      protection law, we encourage you to contact us first so that we have the opportunity to investigate and
      resolve your concerns.
    </p>
    <p>You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO).</p>
    <p>The ICO is the United Kingdom's independent supervisory authority responsible for enforcing data protection legislation.</p>
    <p>Exercising your right to complain to the ICO does not affect any other legal rights or remedies available to you.</p>

    <h3>Identity Verification</h3>
    <p>Before responding to requests involving personal information, we may ask you to provide sufficient information to verify your identity.</p>
    <p>This helps protect your personal information from unauthorised disclosure and ensures that requests are made by the correct individual or an authorised representative.</p>
    <p>Where a representative acts on your behalf, we may request evidence of their authority to act for you.</p>

    <h3>Responding to Requests</h3>
    <p>
      We aim to respond to all valid privacy requests without undue delay and, in most cases, within one month of
      receiving both your request and any information reasonably required to verify your identity.
    </p>
    <p>Where requests are particularly complex or numerous, we may extend this period where permitted by law. If we do so, we will inform you of the reason for the extension.</p>
    <p>Requests that are manifestly unfounded, excessive or repetitive may be refused or may be subject to a reasonable administrative fee where permitted by law.</p>

    <h3>Contacting Us About Your Rights</h3>
    <p>
      If you wish to exercise any of your privacy rights or have questions about how we process your personal
      information, please contact Hove Retail Limited using the contact details provided in this Privacy Policy.
    </p>
    <p>We are committed to handling all requests fairly, transparently and in accordance with applicable UK data protection legislation.</p>
  </section>

  <section id="law-enforcement-requests">
    <h2>Law Enforcement &amp; Regulatory Requests</h2>
    <p>Safeword is committed to protecting the privacy of its members while complying with applicable laws and lawful requests made by competent authorities.</p>
    <p>
      Hove Retail Limited will only disclose personal information where we are satisfied that we have a legal
      obligation or lawful authority to do so, or where disclosure is otherwise permitted by applicable law.
    </p>
    <p>We carefully consider every request for personal information and seek to balance our legal obligations with our commitment to protecting the privacy and rights of our members.</p>

    <h3>Requests We May Receive</h3>
    <p>Safeword may receive requests for information from:</p>
    <ul>
      <li>UK police forces;</li>
      <li>the National Crime Agency (NCA);</li>
      <li>the Information Commissioner's Office (ICO);</li>
      <li>HM Revenue &amp; Customs (HMRC);</li>
      <li>UK courts and tribunals;</li>
      <li>regulatory authorities;</li>
      <li>safeguarding organisations;</li>
      <li>government departments;</li>
      <li>overseas authorities through appropriate legal channels; and</li>
      <li>other competent authorities acting within their lawful powers.</li>
    </ul>
    <p>Not every request will result in disclosure.</p>

    <h3>Assessing Requests</h3>
    <p>Before disclosing personal information, we may consider:</p>
    <ul>
      <li>whether the requesting organisation has lawful authority;</li>
      <li>whether the request is valid and legally binding;</li>
      <li>whether the information requested is relevant and proportionate;</li>
      <li>whether disclosure is necessary;</li>
      <li>whether the request complies with applicable law;</li>
      <li>the privacy rights of affected individuals; and</li>
      <li>any legal restrictions that apply.</li>
    </ul>
    <p>Where appropriate, we may seek independent legal advice before responding to a request.</p>

    <h3>Court Orders and Legally Binding Requests</h3>
    <p>Where we receive a valid court order or other legally binding legal requirement requiring disclosure, we may provide the information specified in that request.</p>
    <p>Where appropriate and lawful, we may seek to limit disclosure to the information reasonably necessary to comply with the legal requirement.</p>

    <h3>Emergency Requests</h3>
    <p>Safeword may disclose personal information without prior notice where we reasonably believe disclosure is necessary to:</p>
    <ul>
      <li>protect life or physical safety;</li>
      <li>prevent serious harm;</li>
      <li>safeguard children or vulnerable individuals;</li>
      <li>prevent serious criminal activity;</li>
      <li>respond to an immediate threat;</li>
      <li>comply with emergency legal obligations; or</li>
      <li>protect the integrity and security of the Services.</li>
    </ul>
    <p>Emergency disclosures will be limited to the information reasonably necessary for the circumstances.</p>

    <h3>Preservation of Information</h3>
    <p>Where required by law or reasonably necessary to support a lawful investigation, Safeword may preserve relevant information before deciding whether disclosure is appropriate.</p>
    <p>Preservation does not necessarily mean that information will be disclosed.</p>
    <p>Information preserved for legal purposes will be retained only for as long as reasonably necessary or as required by law.</p>

    <h3>Regulatory Cooperation</h3>
    <p>Safeword may cooperate with regulatory authorities where required by law.</p>
    <p>This may include responding to lawful enquiries relating to:</p>
    <ul>
      <li>data protection;</li>
      <li>consumer protection;</li>
      <li>financial regulation;</li>
      <li>online safety;</li>
      <li>fraud prevention;</li>
      <li>safeguarding;</li>
      <li>taxation;</li>
      <li>intellectual property; and</li>
      <li>other matters falling within the lawful powers of the relevant authority.</li>
    </ul>

    <h3>International Requests</h3>
    <p>Where requests originate from authorities outside the United Kingdom, Safeword will generally require that they are made through recognised legal channels or supported by an appropriate legal basis before considering disclosure.</p>
    <p>We will assess international requests in accordance with applicable UK law and our legal obligations.</p>

    <h3>User Notification</h3>
    <p>Where legally permitted and where doing so would not:</p>
    <ul>
      <li>prejudice an investigation;</li>
      <li>create a risk of harm;</li>
      <li>breach a court order;</li>
      <li>breach legal restrictions; or</li>
      <li>otherwise be unlawful,</li>
    </ul>
    <p>Safeword may seek to notify affected members before disclosing their personal information.</p>
    <p>There may be circumstances where we are prohibited by law from providing such notice.</p>

    <h3>Requests We May Decline</h3>
    <p>Safeword may refuse or challenge requests that we reasonably believe are:</p>
    <ul>
      <li>unlawful;</li>
      <li>invalid;</li>
      <li>overly broad;</li>
      <li>disproportionate;</li>
      <li>insufficiently supported by legal authority; or</li>
      <li>inconsistent with applicable law.</li>
    </ul>
    <p>Where appropriate, we may request clarification or additional information before responding.</p>

    <h3>Information That May Be Disclosed</h3>
    <p>Where disclosure is legally required or otherwise permitted, information provided may include only that which is reasonably necessary for the specific purpose.</p>
    <p>Depending on the circumstances, this may include:</p>
    <ul>
      <li>account registration information;</li>
      <li>account status;</li>
      <li>transaction records;</li>
      <li>communications where legally required;</li>
      <li>IP address information;</li>
      <li>login records;</li>
      <li>moderation records;</li>
      <li>creator verification records;</li>
      <li>payment references;</li>
      <li>content preserved for investigation; or</li>
      <li>other information specifically required by the lawful request.</li>
    </ul>
    <p>We will not disclose more information than is reasonably necessary to comply with our legal obligations.</p>

    <h3>Transparency</h3>
    <p>Safeword believes transparency is an important aspect of protecting user privacy.</p>
    <p>Where legally permissible, we aim to:</p>
    <ul>
      <li>carefully review all requests for information;</li>
      <li>minimise unnecessary disclosure;</li>
      <li>protect the rights of our members;</li>
      <li>document our responses to lawful requests; and</li>
      <li>continually review our legal compliance procedures.</li>
    </ul>
    <p>We remain committed to balancing our legal responsibilities with our responsibility to protect the privacy and confidentiality of our community.</p>

    <h3>Our Commitment</h3>
    <p>Hove Retail Limited recognises that members trust Safeword with highly personal information.</p>
    <p>We are committed to ensuring that requests for personal information are handled responsibly, lawfully and proportionately.</p>
    <p>Our objective is to comply with applicable legal obligations while safeguarding the privacy, dignity and rights of our members wherever possible.</p>
  </section>

  <section id="marketing-communications">
    <h2>Marketing Communications</h2>
    <p>
      Safeword may communicate with members about the Services, account activity, platform updates, community news
      and, where permitted by law, promotional information relating to Safeword.
    </p>
    <p>
      We respect your communication preferences and are committed to ensuring that marketing communications are
      sent lawfully, transparently and in accordance with the UK General Data Protection Regulation (UK GDPR), the
      Data Protection Act 2018 and the Privacy and Electronic Communications Regulations (PECR).
    </p>

    <h3>Service Communications</h3>
    <p>Some communications are necessary for the operation of your account and the Services.</p>
    <p>These communications are not marketing communications and cannot generally be opted out of while your account remains active.</p>
    <p>Service communications may include:</p>
    <ul>
      <li>account verification;</li>
      <li>password reset requests;</li>
      <li>login notifications;</li>
      <li>security alerts;</li>
      <li>payment confirmations;</li>
      <li>subscription renewals;</li>
      <li>purchase receipts;</li>
      <li>creator payment notifications;</li>
      <li>moderation decisions;</li>
      <li>appeals decisions;</li>
      <li>important changes to the Services;</li>
      <li>updates to our Terms of Service;</li>
      <li>updates to this Privacy Policy;</li>
      <li>account support responses; and</li>
      <li>other communications necessary to administer your account.</li>
    </ul>
    <p>
      (Payment confirmations, subscription renewals, purchase receipts and creator payment notifications apply once
      paid subscriptions/marketplace features launch.)
    </p>
    <p>These communications are processed as part of our contractual obligations to you or to comply with our legal obligations.</p>

    <h3>Promotional Communications</h3>
    <p>Where permitted by law, Safeword may send information about:</p>
    <ul>
      <li>new platform features;</li>
      <li>product announcements;</li>
      <li>creator tools;</li>
      <li>community events;</li>
      <li>educational resources;</li>
      <li>promotional offers;</li>
      <li>competitions or prize draws (where applicable);</li>
      <li>surveys and feedback requests;</li>
      <li>newsletters; and</li>
      <li>other information that may be of interest to our members.</li>
    </ul>
    <p>We will only send marketing communications where we have an appropriate lawful basis to do so.</p>

    <h3>Communication Channels</h3>
    <p>Depending on your account preferences and the Services you use, Safeword may communicate with you using:</p>
    <ul>
      <li>email;</li>
      <li>in-platform notifications;</li>
      <li>mobile application notifications (if available);</li>
      <li>SMS or text messages where you have provided your number and where permitted by law;</li>
      <li>push notifications (where available and enabled); or</li>
      <li>other communication methods that you have chosen to receive.</li>
    </ul>
    <p>The communication channels available may change as Safeword develops new features and services.</p>

    <h3>Consent</h3>
    <p>Where your consent is required before we send marketing communications, we will ask for that consent clearly and separately from other matters.</p>
    <p>Your consent is entirely voluntary.</p>
    <p>Choosing not to receive marketing communications will not affect your ability to use the Safeword platform or access the core Services.</p>
    <p>You may withdraw your consent at any time.</p>
    <p>Withdrawal of consent does not affect the lawfulness of communications sent before consent was withdrawn.</p>

    <h3>Legitimate Interests</h3>
    <p>
      In some circumstances, Safeword may send information about products or services that are similar to those you
      have already requested or purchased, where this is permitted under applicable law.
    </p>
    <p>Where we rely on legitimate interests or the "soft opt-in" provisions under PECR, we will always provide a simple method for you to opt out of future marketing communications.</p>

    <h3>Managing Your Preferences</h3>
    <p>Members may manage their communication preferences at any time through their account settings where available. You may choose to:</p>
    <ul>
      <li>unsubscribe from marketing emails;</li>
      <li>disable promotional notifications;</li>
      <li>manage push notification preferences;</li>
      <li>update email preferences;</li>
      <li>change communication settings; or</li>
      <li>contact us to request that marketing communications stop.</li>
    </ul>
    <p>Please note that changes to your preferences may take a reasonable period to become effective.</p>

    <h3>Unsubscribing</h3>
    <p>Every marketing email sent by Safeword will include an option allowing you to unsubscribe from future marketing communications.</p>
    <p>You may also contact us directly if you no longer wish to receive marketing communications.</p>
    <p>Once your request has been processed, we will stop sending marketing communications unless you later choose to opt in again.</p>
    <p>You may continue to receive essential service communications relating to your account.</p>

    <h3>Surveys and Feedback</h3>
    <p>From time to time, Safeword may invite members to participate in surveys, questionnaires or feedback requests to help improve the Services.</p>
    <p>Participation is entirely voluntary.</p>
    <p>Unless otherwise stated, choosing not to participate will not affect your use of the platform.</p>
    <p>Survey responses may be analysed in aggregate or anonymised form to help improve our products and services.</p>

    <h3>Third-Party Marketing</h3>
    <p>Safeword does not sell, rent or disclose your personal information to third parties for their own direct marketing purposes.</p>
    <p>We do not permit third parties to use your personal information to send you marketing communications unrelated to the operation of the Safeword platform.</p>
    <p>Where trusted service providers assist us in delivering our communications, they do so solely on our behalf and in accordance with contractual obligations requiring them to protect your personal information.</p>

    <h3>Changes to Marketing Practices</h3>
    <p>As Safeword develops new products, services and communication methods, we may update how we communicate with members.</p>
    <p>Where required by law, we will update this Privacy Policy and obtain any additional consent necessary before introducing new marketing practices.</p>

    <h3>Our Commitment</h3>
    <p>Safeword believes that marketing communications should always be relevant, respectful and transparent.</p>
    <p>
      We are committed to giving members meaningful control over how they receive communications from us and to
      ensuring that all marketing activities are carried out responsibly and in accordance with applicable UK data
      protection and electronic communications legislation.
    </p>
  </section>

  <section id="changes-to-this-policy">
    <h2>Changes to this Privacy Policy</h2>
    <p>Safeword is committed to maintaining a Privacy Policy that accurately reflects how we collect, use, protect and manage personal information.</p>
    <p>
      As our Services evolve, technology develops and legal or regulatory requirements change, it may be necessary
      for Hove Retail Limited to update this Privacy Policy from time to time.
    </p>
    <p>We encourage all members to review this Privacy Policy periodically to remain informed about how we protect their personal information.</p>

    <h3>Why We May Update This Privacy Policy</h3>
    <p>We may update this Privacy Policy for a variety of reasons, including:</p>
    <ul>
      <li>changes to applicable laws or regulations;</li>
      <li>guidance issued by regulators;</li>
      <li>developments in UK data protection legislation;</li>
      <li>changes to the Safeword platform or Services;</li>
      <li>introduction of new products or features;</li>
      <li>changes to payment processing arrangements;</li>
      <li>changes to age verification procedures;</li>
      <li>improvements to security measures;</li>
      <li>changes to moderation practices;</li>
      <li>improvements to our privacy practices;</li>
      <li>changes to third-party service providers; or</li>
      <li>other operational or business requirements.</li>
    </ul>

    <h3>How We Will Notify Members</h3>
    <p>Where we make material changes to this Privacy Policy, we will take reasonable steps to notify affected members.</p>
    <p>Depending on the nature of the changes, notification may be provided through:</p>
    <ul>
      <li>notices displayed within the Safeword platform;</li>
      <li>email notifications;</li>
      <li>account notifications;</li>
      <li>announcements on our website;</li>
      <li>updates within the mobile application (if applicable); or</li>
      <li>other appropriate communication channels.</li>
    </ul>
    <p>Minor editorial changes or updates that do not materially affect your rights or how your personal information is processed may be made without individual notification.</p>

    <h3>Effective Date</h3>
    <p>Each version of this Privacy Policy will include an effective date showing when it came into force.</p>
    <p>Where appropriate, we may also include a revision date or version number to assist members in identifying the most recent version.</p>
    <p>The latest version of this Privacy Policy will always be available through the Safeword website and, where applicable, within the Safeword application.</p>

    <h3>Continued Use of the Services</h3>
    <p>
      Where permitted by applicable law, your continued use of the Services after the effective date of an updated
      Privacy Policy constitutes your acknowledgement that the Privacy Policy has been updated.
    </p>
    <p>Where changes require your consent under applicable law, we will seek that consent before relying on the updated processing activity.</p>
    <p>
      Nothing in this section limits any rights you may have under the UK General Data Protection Regulation (UK
      GDPR), the Data Protection Act 2018 or any other applicable legislation.
    </p>

    <h3>Historic Versions</h3>
    <p>Safeword may retain previous versions of this Privacy Policy for legal, regulatory, operational or record-keeping purposes.</p>
    <p>Where appropriate, previous versions may be made available upon reasonable request where this is necessary to understand how your personal information was processed at a particular time.</p>

    <h3>Questions About Changes</h3>
    <p>
      If you have any questions regarding changes to this Privacy Policy or how they affect the processing of your
      personal information, you may contact Hove Retail Limited using the contact details provided in the "Contact
      Us" section of this Privacy Policy.
    </p>

    <h3>Our Commitment to Transparency</h3>
    <p>Safeword believes that transparency is fundamental to building trust.</p>
    <p>
      Whenever we make significant changes to how we collect, use, share or protect personal information, we will
      seek to communicate those changes openly and in a manner that helps members understand how their information
      may be affected.
    </p>
    <p>
      Our objective is to ensure that this Privacy Policy remains accurate, accessible and consistent with our
      commitment to privacy, consent, accountability and lawful adult expression.
    </p>
  </section>

  <section id="contact-us">
    <h2>Contact Us</h2>
    <p>Safeword welcomes questions, comments and requests relating to this Privacy Policy, your personal information and your privacy rights.</p>
    <p>
      If you have any questions about how Hove Retail Limited collects, uses, stores or protects your personal
      information, or if you wish to exercise any of your rights under the UK General Data Protection Regulation (UK
      GDPR) or the Data Protection Act 2018, please contact us.
    </p>

    <h3>Data Controller</h3>
    <p>The Data Controller responsible for the processing of personal information through the Safeword platform is:</p>
    <p>
      <strong>Hove Retail Limited</strong><br>
      Company number: 11099033<br>
      Registered office: 12a Marlborough Place, Brighton, England, BN1 1WN<br>
      Operating the Safeword platform.
    </p>

    <h3>Data Protection Officer / Privacy Contact</h3>
    <p>Please direct all privacy enquiries to our General Privacy Enquiries contact below.</p>

    <h3>General Privacy Enquiries</h3>
    <p>For all privacy-related enquiries, data protection requests or questions regarding this Privacy Policy, please contact us at:</p>
    <p>Email: <a href="mailto:contact@safeword.co.uk">contact&#64;safeword.co.uk</a></p>
    <p>We aim to acknowledge enquiries as soon as reasonably practicable and, where applicable, respond within the timeframes required by UK data protection legislation.</p>

    <h3>Exercising Your Privacy Rights</h3>
    <p>You may contact us at any time if you wish to:</p>
    <ul>
      <li>request access to your personal information;</li>
      <li>request correction of inaccurate or incomplete information;</li>
      <li>request deletion of your personal information where applicable;</li>
      <li>request restriction of processing;</li>
      <li>object to certain processing activities;</li>
      <li>withdraw consent where processing is based on consent;</li>
      <li>request a copy of your personal information in a portable format where applicable;</li>
      <li>ask questions about how your information is processed; or</li>
      <li>raise any other privacy-related concern.</li>
    </ul>
    <p>To help protect your personal information, we may ask you to verify your identity before processing your request.</p>

    <h3>Complaints</h3>
    <p>We are committed to resolving privacy concerns fairly, promptly and transparently.</p>
    <p>
      If you have a concern about how we have handled your personal information, we encourage you to contact us
      first so that we have the opportunity to investigate the matter and work towards a satisfactory resolution.
    </p>

    <h3>Information Commissioner's Office (ICO)</h3>
    <p>
      If you believe that your personal information has been processed in a manner that does not comply with
      applicable UK data protection law, you have the right to lodge a complaint with the Information Commissioner's
      Office (ICO), the United Kingdom's independent authority responsible for upholding information rights.
    </p>
    <p>You may contact the ICO directly if you are dissatisfied with our response or if you believe your concerns have not been resolved appropriately.</p>
    <p>Making a complaint to the ICO does not affect any other legal rights or remedies that may be available to you.</p>

    <h3>Keeping Your Contact Information Up to Date</h3>
    <p>To help ensure that we can communicate with you effectively, you are responsible for keeping the contact information associated with your Safeword account accurate and up to date.</p>
    <p>You can usually update your contact details through your account settings or by contacting our support team.</p>

    <h3>Our Commitment</h3>
    <p>Hove Retail Limited is committed to protecting your privacy, respecting your rights and handling your personal information responsibly.</p>
    <p>We welcome questions about our privacy practices and will always seek to respond in a clear, transparent and respectful manner.</p>
    <p>
      Safeword is built on trust, consent, accountability and respect for our community, and we remain committed to
      maintaining the highest standards of privacy and data protection across our Services.
    </p>
  </section>

  <section id="our-commitment">
    <h2>Our Commitment to Privacy, Consent &amp; Community Safety</h2>
    <p>
      Safeword was created to provide a trusted online community for consenting adults to connect, communicate,
      learn, create and share within the BDSM, fetish and kink communities.
    </p>
    <p>
      We recognise that many of our members choose Safeword because privacy, discretion, consent and trust are
      fundamental to their lives. We understand the responsibility that comes with operating a platform where
      members may choose to share highly personal information, intimate content and sensitive aspects of their
      identity. Protecting that trust is central to everything we do.
    </p>

    <h3>Privacy</h3>
    <p>
      We believe that privacy is a fundamental right. Safeword is committed to collecting and processing only the
      personal information that is reasonably necessary to provide our Services, comply with legal obligations and
      protect our community.
    </p>
    <p>We do not sell our members' personal information to advertisers, data brokers or other third parties for their own marketing purposes.</p>

    <h3>Consent</h3>
    <p>
      Consent is one of the core principles upon which the Safeword community is built. We expect all members to
      respect the principles of informed, voluntary and ongoing consent in their interactions with others, both
      online and offline.
    </p>
    <p>
      Creators are responsible for ensuring that all identifiable individuals appearing in content published
      through the Services are adults who have freely consented to both the creation and publication of that
      content. Content that appears to breach these principles may be investigated and removed, and appropriate
      enforcement action may be taken.
    </p>

    <h3>Community Safety</h3>
    <p>
      We are committed to providing an environment that promotes lawful adult expression while protecting members
      from illegal activity, abuse and exploitation. Safeword supports the right of consenting adults to engage in
      lawful discussion, education, creativity and self-expression, and we strive to treat members of our diverse
      BDSM, fetish and kink community with dignity, fairness and respect.
    </p>
    <p>
      Safeword uses a combination of age verification procedures, AI-assisted technologies, trained human
      moderators, member reporting tools, fraud prevention systems, security monitoring and clearly published
      Community Standards to help maintain the safety, integrity and reliability of the platform. We will take
      appropriate action where content or behaviour breaches our Terms of Service, Community Standards or
      applicable law.
    </p>

    <h3>Transparency</h3>
    <p>
      We believe members should understand how their personal information is collected, used, shared and protected,
      and we aim to explain our practices clearly and avoid unnecessary complexity.
    </p>

    <h3>Accountability</h3>
    <p>
      Safeword is committed to acting responsibly in the way we manage personal information and moderate the
      platform. We regularly review our internal procedures, security measures, moderation practices and compliance
      processes, recognising that maintaining trust requires continual improvement rather than a one-time
      commitment.
    </p>
    <p>
      Creating a safe and trusted community is a shared responsibility. We ask every member to act honestly and
      respectfully, respect the privacy and consent of others, report suspected breaches of our policies, protect
      their own account security, comply with applicable laws, and contribute positively to the Safeword community.
    </p>

    <h3>Our Ongoing Commitment</h3>
    <p>
      Hove Retail Limited is committed to operating Safeword responsibly, ethically and transparently. As
      technology, legislation and the needs of our community evolve, we will continue to improve our platform,
      strengthen our privacy and security practices and refine our safeguarding measures.
    </p>
    <p>
      Our objective is to provide a trusted platform where consenting adults can participate with confidence,
      knowing that their privacy, dignity and personal information are treated with care and respect.
    </p>
    <p>Thank you for placing your trust in Safeword.</p>
  </section>

</app-legal-page-layout>
